import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FileText, Trash2, Upload, FolderClosed } from "lucide-react";
import { CareShell } from "@/components/CareShell";
import { RecordPreviewDialog } from "@/components/RecordPreviewDialog";
import { RECORD_CATEGORIES } from "@/lib/constants";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import api, { apiErrorMessage } from "@/lib/api";

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function Records() {
  const inputRef = useRef(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [uploadCategory, setUploadCategory] = useState("Other");
  const [filterCat, setFilterCat] = useState("");

  const load = () =>
    api.get("/documents").then(({ data }) => setDocs(data || [])).finally(() => setLoading(false));

  useEffect(() => {
    load();
  }, []);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) {
      toast.error("File too large (max 6MB).");
      return;
    }
    setUploading(true);
    try {
      const dataUrl = await fileToDataUrl(file);
      await api.post("/documents", {
        label: file.name,
        file_type: file.type || "Document",
        data_url: dataUrl,
        category: uploadCategory,
      });
      toast.success("Record added.");
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/documents/${id}`);
      setDocs((prev) => prev.filter((d) => d.id !== id));
      toast.success("Record removed.");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  return (
    <CareShell active="records" title="Records">
      <p className="text-[#A1A1AA]">Your medical documents, shared securely with specialists you choose.</p>

      <input
        ref={inputRef}
        type="file"
        onChange={onPick}
        className="hidden"
        data-testid="records-file-input"
        accept="image/*,application/pdf,.doc,.docx"
      />

      <label className="block text-sm font-medium text-[#A1A1AA] mt-4 mb-1.5">Category for next upload</label>
      <select
        data-testid="records-upload-category"
        value={uploadCategory}
        onChange={(e) => setUploadCategory(e.target.value)}
        className="w-full bg-[#1A1A1A] border border-[#333333] rounded-xl px-4 py-3.5 min-h-[44px] text-[#F8FAFC] focus:outline-none focus:border-[#EF9F27]"
      >
        {RECORD_CATEGORIES.map((c) => (
          <option key={c} value={c}>{c}</option>
        ))}
      </select>

      <button
        data-testid="records-add-btn"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="mt-3 w-full rounded-full bg-[#EF9F27] text-[#111111] font-bold px-6 py-3.5 min-h-[48px] flex items-center justify-center gap-2 transition-transform active:scale-[0.97] disabled:opacity-60"
      >
        <Upload size={18} /> {uploading ? "Uploading…" : "Add a record"}
      </button>

      {docs.length > 0 && (
        <select
          data-testid="records-filter-category"
          value={filterCat}
          onChange={(e) => setFilterCat(e.target.value)}
          className="mt-4 w-full bg-[#111111] border border-[#333333] rounded-xl px-4 py-3 min-h-[44px] text-[#F8FAFC] focus:outline-none focus:border-[#EF9F27]"
        >
          <option value="">All categories</option>
          {RECORD_CATEGORIES.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      )}

      <div className="mt-6">
        {loading ? (
          <div className="space-y-3">
            {[0, 1].map((i) => (
              <div key={i} className="h-20 bg-[#1A1A1A] border border-[#333333] rounded-2xl animate-pulse" />
            ))}
          </div>
        ) : docs.length === 0 ? (
          <div className="bg-[#1A1A1A] border border-dashed border-[#333333] rounded-2xl p-8 text-center">
            <FolderClosed className="mx-auto text-[#666666]" size={32} />
            <p className="mt-3 text-[#F8FAFC] font-bold">No records yet</p>
            <p className="text-sm text-[#A1A1AA] mt-1">Add test results, scans or reports.</p>
          </div>
        ) : (
          <ul className="space-y-3" data-testid="records-list">
            {docs.filter((d) => !filterCat || (d.category || "Other") === filterCat).map((d) => (
              <li
                key={d.id}
                className="bg-[#1A1A1A] border border-[#333333] rounded-2xl p-4 flex items-center gap-3"
              >
                <div className="w-10 h-10 rounded-xl bg-[#EF9F27]/15 flex items-center justify-center shrink-0">
                  <FileText size={18} className="text-[#EF9F27]" />
                </div>
                <button
                  data-testid={`records-preview-${d.id}`}
                  onClick={() => setPreviewDoc(d.id)}
                  className="min-w-0 flex-1 text-left"
                >
                  <p className="font-medium text-[#F8FAFC] truncate">{d.label}</p>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span
                      data-testid={`records-category-${d.id}`}
                      className="bg-[#EF9F27]/15 text-[#EF9F27] px-2 py-0.5 rounded-full text-[11px] font-bold"
                    >
                      {d.category || "Other"}
                    </span>
                    <span className="text-xs text-[#A1A1AA]">
                      {d.uploaded_at ? new Date(d.uploaded_at).toLocaleDateString() : ""}
                    </span>
                  </div>
                </button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button
                      data-testid={`records-delete-${d.id}`}
                      aria-label={`Delete ${d.label}`}
                      className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-xl text-[#EF4444] hover:bg-[#EF4444]/10 transition-colors"
                    >
                      <Trash2 size={18} />
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="max-w-[360px] rounded-2xl bg-[#1A1A1A] border-[#333333] text-[#F8FAFC]">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete this record?</AlertDialogTitle>
                      <AlertDialogDescription className="text-[#A1A1AA]">
                        "{d.label}" will be permanently removed.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="min-h-[48px] rounded-xl bg-[#222222] border-[#333333] text-[#F8FAFC]">
                        Cancel
                      </AlertDialogCancel>
                      <AlertDialogAction
                        data-testid={`records-confirm-delete-${d.id}`}
                        onClick={() => remove(d.id)}
                        className="min-h-[48px] rounded-xl bg-[#EF4444] hover:bg-[#dc2626]"
                      >
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </li>
            ))}
          </ul>
        )}
      </div>

      <RecordPreviewDialog docId={previewDoc} open={!!previewDoc} onClose={() => setPreviewDoc(null)} />
    </CareShell>
  );
}
